/*
  Stockist locator (master list #36). Enhances sections/lp-stockists.liquid:
  country tabs, instant text filter, "Search" for a town/postcode (OpenStreetMap
  Nominatim, one request per search) or "Near me" (browser location) to sort by
  distance, and a Leaflet map loaded from cdnjs only when the section is near the screen.
*/
(() => {
  const LEAFLET = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/';
  const CC = { Australia: 'au', 'United Kingdom': 'gb', 'United States': 'us', 'New Zealand': 'nz' };
  const MILES = { 'United Kingdom': true, 'United States': true };

  // LP monogram pin (option B, chosen 6 Oct 2026): white badge normally, bronze when selected.
  const pinSvg = (selected) => {
    const bg = selected ? '#8a6d3b' : '#ffffff';
    const fg = selected ? '#ffffff' : '#1d1d1d';
    const edge = selected ? '#ffffff' : '#1d1d1d';
    return '<svg class="lp-stk__lp-pin" width="' + (selected ? 40 : 32) + '" height="' + (selected ? 50 : 40) + '" viewBox="0 0 32 40" aria-hidden="true">' +
      '<path d="M11.5 29 16 38.5 20.5 29z" fill="' + (selected ? bg : '#1d1d1d') + '"/>' +
      '<circle cx="16" cy="16" r="15" fill="' + bg + '" stroke="' + edge + '" stroke-width="' + (selected ? 1.5 : 1.2) + '"/>' +
      '<circle cx="16" cy="16" r="11.6" fill="none" stroke="' + fg + '" stroke-width=".6" opacity=".85"/>' +
      '<text x="16" y="19.7" dx="0.8" text-anchor="middle" font-family="Helvetica Neue, Helvetica, Arial, sans-serif" font-size="10" letter-spacing="1.6" fill="' + fg + '">LP</text></svg>';
  };

  const loadLeaflet = () => {
    if (window.L) return Promise.resolve(window.L);
    if (loadLeaflet.p) return loadLeaflet.p;
    const css = document.createElement('link');
    css.rel = 'stylesheet';
    css.href = LEAFLET + 'leaflet.min.css';
    document.head.appendChild(css);
    loadLeaflet.p = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = LEAFLET + 'leaflet.min.js';
      s.onload = () => resolve(window.L);
      s.onerror = reject;
      document.head.appendChild(s);
    });
    return loadLeaflet.p;
  };

  const km = (a, b) => {
    const R = 6371, r = Math.PI / 180;
    const dLat = (b.lat - a.lat) * r, dLng = (b.lng - a.lng) * r;
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * r) * Math.cos(b.lat * r) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
  };

  const init = (root) => {
    const tabs = Array.from(root.querySelectorAll('.lp-stk__tab'));
    const list = root.querySelector('.lp-stk__list');
    const cards = Array.from(root.querySelectorAll('[data-store]'));
    const groups = Array.from(root.querySelectorAll('[data-group]'));
    const empty = root.querySelector('.lp-stk__empty');
    const input = root.querySelector('.lp-stk__search input');
    const form = root.querySelector('.lp-stk__search');
    const nearBtn = root.querySelector('[data-near]');
    const status = root.querySelector('.lp-stk__status');
    const mapEl = root.querySelector('.lp-stk__map');
    const stores = cards.map((el) => ({
      el,
      country: el.dataset.country,
      lat: parseFloat(el.dataset.lat),
      lng: parseFloat(el.dataset.lng),
      text: el.dataset.search || '',
      name: el.querySelector('.lp-stk__name span').textContent,
      addr: el.querySelector('.lp-stk__addr').textContent,
    }));
    const original = Array.from(list.children).filter((el) => !el.classList.contains('lp-stk__empty')); // headings + cards, server order

    let country = (tabs.find((t) => t.getAttribute('aria-selected') === 'true') || tabs[0] || {}).dataset?.country;
    // ?tab=uk|us|nz|au in the link opens that tab (not ?country=, which Shopify uses to switch market/currency) (adam, 7 Oct 2026: homepage FAQ "Do you ship to the UK/USA" links).
    const URL_COUNTRY = { uk: 'United Kingdom', gb: 'United Kingdom', us: 'United States', usa: 'United States', nz: 'New Zealand', au: 'Australia' };
    const wanted = URL_COUNTRY[(new URLSearchParams(location.search).get('tab') || '').toLowerCase()];
    if (wanted && tabs.some((t) => t.dataset.country === wanted)) {
      country = wanted;
      tabs.forEach((t) => t.setAttribute('aria-selected', String(t.dataset.country === wanted)));
    }
    let origin = null; // {lat,lng,label} when sorting by distance
    let map = null, markers = new Map(), active = null;
    const icons = {};

    const visible = () => stores.filter((s) => s.el.hidden === false);

    const render = () => {
      const q = (input.value || '').trim().toLowerCase();
      const textFilter = !origin && q.length > 0;
      stores.forEach((s) => {
        const show = s.country === country && (!textFilter || s.text.includes(q));
        s.el.hidden = !show;
        const d = s.el.querySelector('[data-dist]');
        if (origin && show && !isNaN(s.lat)) {
          const k = km(origin, s);
          s.dist = k;
          d.textContent = MILES[country] ? `${(k * 0.621).toFixed(k < 16 ? 1 : 0)} mi` : `${k.toFixed(k < 10 ? 1 : 0)} km`;
        } else {
          s.dist = null;
          d.textContent = '';
        }
      });
      // Distance mode: flat list, nearest first. Otherwise: original order with state headings.
      if (origin) {
        visible().sort((a, b) => (a.dist ?? 1e9) - (b.dist ?? 1e9)).forEach((s) => list.insertBefore(s.el, empty));
        groups.forEach((g) => (g.hidden = true));
      } else {
        original.forEach((el) => list.insertBefore(el, empty));
        groups.forEach((g) => {
          let n = g.nextElementSibling, any = false;
          while (n && n.matches('[data-store]')) { if (!n.hidden) any = true; n = n.nextElementSibling; }
          g.hidden = g.dataset.country !== country || !any || textFilter;
        });
      }
      empty.hidden = visible().length > 0;
      updateMap();
    };


    const setCountry = (c) => {
      country = c;
      tabs.forEach((t) => t.setAttribute('aria-selected', String(t.dataset.country === c)));
    };

    const updateMap = () => {
      if (!map) return;
      const L = window.L;
      const vis = visible().filter((s) => !isNaN(s.lat));
      markers.forEach((m, s) => { if (!vis.includes(s)) m.remove(); else m.addTo(map); });
      const pts = (origin ? vis.slice(0, 8) : vis).map((s) => [s.lat, s.lng]);
      if (origin) pts.push([origin.lat, origin.lng]);
      if (pts.length) map.fitBounds(L.latLngBounds(pts).pad(0.15), { maxZoom: 13, animate: false });
    };

    const activate = (s, fromMap) => {
      if (active) { active.el.classList.remove('is-active'); const pm = markers.get(active); if (pm) { pm.setIcon(icons.normal); pm.setZIndexOffset(0); } }
      active = s;
      s.el.classList.add('is-active');
      const m = markers.get(s);
      if (m && map) {
        m.setIcon(icons.selected);
        m.setZIndexOffset(1000);
        if (!fromMap) map.setView([s.lat, s.lng], Math.max(map.getZoom(), 12));
        m.openPopup();
      }
      if (fromMap) s.el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    };

    const buildMap = () =>
      loadLeaflet().then((L) => {
        map = L.map(mapEl, { scrollWheelZoom: false, zoomControl: true });
        map.attributionControl.setPrefix('');
        L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 18,
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>',
        }).addTo(map);
        // Selected pin is 25% bigger; anchors keep the point on the same spot.
        const mk = (sel) => L.divIcon({ className: 'lp-stk__pin-wrap', html: pinSvg(sel), iconSize: sel ? [40, 50] : [32, 40], iconAnchor: sel ? [20, 48.75] : [16, 39], popupAnchor: [0, sel ? -47 : -38] });
        icons.normal = mk(false);
        icons.selected = mk(true);
        const icon = icons.normal;
        stores.forEach((s) => {
          if (isNaN(s.lat)) return;
          const esc = (t) => t.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
          const m = L.marker([s.lat, s.lng], { icon, title: s.name }).bindPopup(`<strong>${esc(s.name)}</strong><br>${esc(s.addr)}`);
          m.on('click', () => activate(s, true));
          markers.set(s, m);
        });
        updateMap();
      }).catch((e) => { console.error('lp-stockists map', e); mapEl.hidden = true; });

    // Map only when the section is near the screen (keeps the page light).
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((e) => { if (e.some((x) => x.isIntersecting)) { io.disconnect(); buildMap(); } }, { rootMargin: '300px' });
      io.observe(mapEl);
    } else buildMap();

    tabs.forEach((t) => t.addEventListener('click', () => { setCountry(t.dataset.country); origin = null; status.textContent = ''; input.value = ''; render(); }));
    cards.forEach((el) => el.addEventListener('click', (e) => { if (e.target.closest('a')) return; activate(stores.find((s) => s.el === el), false); }));
    input.addEventListener('input', () => { if (origin) { origin = null; status.textContent = ''; } render(); });

    const geocode = async (q) => {
      const cc = CC[country] || 'au,gb,us,nz';
      const r = await fetch(`https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&countrycodes=${cc}&q=${encodeURIComponent(q)}`, { headers: { 'Accept-Language': 'en' } });
      const j = await r.json();
      return j[0] ? { lat: +j[0].lat, lng: +j[0].lon, label: j[0].display_name.split(',').slice(0, 2).join(',') } : null;
    };

    form.addEventListener('submit', async () => {
      const q = input.value.trim();
      if (!q) return;
      origin = null;
      // A store-name match: just filter. Otherwise treat it as a place (town/suburb/postcode) and sort by distance.
      const ql = q.toLowerCase();
      const byName = stores.filter((s) => s.country === country && s.name.toLowerCase().includes(ql));
      if (byName.length) {
        render();
        status.textContent = `${byName.length} store${byName.length > 1 ? 's' : ''} matching “${q}”`;
        return;
      }
      status.textContent = 'Searching…';
      try {
        const place = await geocode(q);
        if (!place) { render(); status.textContent = visible().length ? `${visible().length} store${visible().length > 1 ? 's' : ''} matching “${q}”` : `Couldn't find “${q}”. Try a town or postcode.`; return; }
        origin = place;
        render();
        status.textContent = `Nearest stores to ${place.label}`;
      } catch (e) {
        status.textContent = 'Search is unavailable right now. Browse the list below.';
      }
    });

    nearBtn.addEventListener('click', () => {
      if (!navigator.geolocation) { status.textContent = 'Location is not available on this device.'; return; }
      status.textContent = 'Finding your location…';
      navigator.geolocation.getCurrentPosition(
        (p) => {
          origin = { lat: p.coords.latitude, lng: p.coords.longitude, label: 'you' };
          // switch to the country with the closest store
          const nearest = stores.filter((s) => !isNaN(s.lat)).sort((a, b) => km(origin, a) - km(origin, b))[0];
          if (nearest) setCountry(nearest.country);
          input.value = '';
          render();
          // Computers without GPS often report the internet provider's location (e.g. a capital city).
          const accKm = Math.round((p.coords.accuracy || 0) / 1000);
          status.textContent = accKm > 5
            ? 'Your device gave an approximate location (within about ' + accKm + ' km). For better results, type your town or postcode.'
            : 'Nearest stores to you';
        },
        () => { status.textContent = 'Location permission was declined. Search by town or postcode instead.'; },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
      );
    });

    render();
  };

  const start = () => document.querySelectorAll('.lp-stk').forEach((el) => { if (!el.dataset.ready) { el.dataset.ready = '1'; init(el); } });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
  document.addEventListener('shopify:section:load', start);
})();
